import queryString from "query-string"
import { setAuthHeader } from "api/service.ts"
import {
  INewBalance,
  IGetBalanceResponse
} from "store/apis/balanceAPI/balance.types"
import {
  IGetReferralsCountResponse,
  ILeaderProgressResponse,
  IRefreshPositionResponse,
  IUser,
  IUserLeaderboardShort,
  IUserStats,
  IGetReferralsByOwnerRes,
  IUserStatistics,
  IUserSettings
} from "store/apis/userAPI/user.types"
import { setSentryUser } from "utils/sentry.ts"
import { IPaginationReq } from "utils/interfaces"
import {
  getTgUser,
  getTgUserId,
  getTgWebApp,
  isTelegramEnv
} from "utils/tgWebApp.ts"
import { TAGS, api } from "../../api.ts"

const basePath = "/users"

export const userApi = api.enhanceEndpoints({
  addTagTypes: [TAGS.USER, TAGS.BALANCE]
})

export const userEndpoints = userApi.injectEndpoints({
  endpoints: (builder) => ({
    auth: builder.mutation<any, void>({
      query: () => {
        if (!isTelegramEnv())
          throw new Error("Telegram auth requires TMA environment")

        const refCode = window.location.search.split("ref_")[1]
        const authUrl = refCode
          ? `/auth/telegram?ref=${refCode}`
          : "/auth/telegram"

        return {
          url: authUrl,
          method: "POST",
          data: {
            initData: window.Telegram.WebApp.initData,
            id: window.Telegram.WebApp.initDataUnsafe.user.id.toString(),
            photo_url:
              window.Telegram.WebApp.initDataUnsafe.user.photo_url?.toString(),
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
          }
        }
      },
      extraOptions: { maxRetries: 5 },
      onQueryStarted: async (_, { queryFulfilled }) => {
        const { data } = await queryFulfilled
        setAuthHeader(data.accessToken)
      }
    }),
    getUserData: builder.query<IUser, void>({
      query: () => ({
        url: "/users/profile",
        method: "GET"
      }),
      providesTags: () => [{ type: TAGS.USER, id: "user" }],
      onQueryStarted: async (_, { queryFulfilled }) => {
        const { data } = await queryFulfilled

        setSentryUser(data._id, undefined, {
          telegram_id: getTgUserId()?.toString(),
          telegram_username: getTgUser()?.username,
          language: getTgUser()?.language_code,
          language_version: getTgWebApp()?.version,
          platform: getTgWebApp()?.platform
        })
      }
    }),
    getReferredUsers: builder.query<
      IGetReferralsByOwnerRes,
      { page: number; limit?: number }
    >({
      query: ({ page, limit }) => ({
        url: `/users/referred-users/?page=${page}&limit=${limit || 6}`,
        method: "GET"
      }),
      providesTags: () => [{ type: TAGS.USER, id: "referrals" }]
    }),
    collectRewardsFromReferrals: builder.mutation<void, void>({
      query: () => ({
        url: "/users/claim-ref-rewards",
        method: "POST"
      }),
      invalidatesTags: () => [
        { type: TAGS.USER, id: "referrals" },
        { type: TAGS.BALANCE }
      ]
    }),
    setDogName: builder.mutation<string, string>({
      query: (dogname) => ({
        url: "/users/set-dogname",
        method: "POST",
        data: { dogname }
      }),
      extraOptions: { maxRetries: 3 }
    }),
    setSoundMute: builder.mutation<boolean, boolean>({
      query: (isSoundMute) => ({
        url: "/users/set-sound-mute",
        method: "POST",
        data: { isSoundMute }
      }),
      extraOptions: { maxRetries: 3 },
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled

        dispatch(
          userEndpoints.util.updateQueryData(
            "getUserSettings",
            undefined,
            (draft: IUserSettings) => {
              draft.isSoundMute = !!data
            }
          )
        )
      }
    }),
    setLang: builder.mutation<string, string>({
      query: (language) => ({
        url: "/users/set-lang",
        method: "POST",
        data: { language }
      }),
      extraOptions: { maxRetries: 3 },
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled

        dispatch(
          userEndpoints.util.updateQueryData(
            "getUserSettings",
            undefined,
            (draft: IUserSettings) => {
              draft.language = data
            }
          )
        )
      }
    }),
    buyPackage: builder.mutation<{ link: string }, string>({
      query: (packageId) => ({
        url: "/users/buy-gold",
        method: "POST",
        data: { packageId }
      })
    }),
    getReferrer: builder.query<IUser, string>({
      query: (refCode) => ({
        url: `/users/referrer?referralCode=${refCode}`,
        method: "GET"
      }),
      extraOptions: { maxRetries: 3 }
    }),
    hideUserName: builder.mutation<boolean, void>({
      query: () => ({
        url: "/users/hide-username",
        method: "PUT"
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled

        dispatch(
          userEndpoints.util.updateQueryData(
            "getUserSettings",
            undefined,
            (draft: IUserSettings) => {
              draft.hideUsername = !!data
            }
          )
        )
      }
    }),
    exchangeStarsToGold: builder.mutation<INewBalance, void>({
      query: () => ({
        url: "/users/exchange/star-gold",
        method: "PATCH"
      }),
      invalidatesTags: () => [{ type: TAGS.BALANCE }]
    }),
    getReferralsCount: builder.query<IGetReferralsCountResponse, number>({
      query: (lvl) => {
        const url = `/users/calc-referred-users/${lvl}`

        return {
          url,
          method: "GET"
        }
      },
      providesTags: (_result, _error, lvl) => [
        { type: TAGS.USER, id: `referred-users-${lvl}` }
      ]
    }),
    getCurrentLeaderProgress: builder.query<
      ILeaderProgressResponse,
      { locationIndex: number; userId: string }
    >({
      query: ({ locationIndex, userId }) => {
        return {
          url: `/users/total-progress/${locationIndex}/${userId}`,
          method: "GET"
        }
      },
      providesTags: (res) => [{ type: TAGS.USER, id: res?.userId }]
    }),
    refreshMyPositionInLeaders: builder.mutation<
      IRefreshPositionResponse,
      number
    >({
      query: (locationIndex: number) => ({
        url: `/users/my-position/${locationIndex}`,
        method: "GET"
      })
    }),
    checkDailyAndInvites: builder.mutation<any, void>({
      query: () => ({
        url: "/users/check-daily-and-invites",
        method: "POST"
      })
    }),
    checkCurrentUserRole: builder.mutation<boolean, void>({
      query: () => ({
        url: "/users/check-role",
        method: "GET"
      })
    }),
    sentVerificationEmailCode: builder.mutation<boolean, string>({
      query: (email) => ({
        url: "/users/send-verification-email-code",
        method: "POST",
        data: { email }
      })
    }),
    sentDetachEmailCode: builder.mutation<boolean, void>({
      query: () => ({
        url: "/users/detach-email",
        method: "GET"
      })
    }),
    verifyEmailCode: builder.mutation<boolean, string>({
      query: (code) => ({
        url: "/users/verify-email-code",
        method: "POST",
        data: { code }
      })
    }),
    verifyDetachEmailCode: builder.mutation<boolean, string>({
      query: (code) => ({
        url: "/users/verify-detach-email",
        method: "POST",
        data: { code }
      })
    }),
    getBalanceHistory: builder.query<IGetBalanceResponse, IPaginationReq>({
      query: ({ page, limit, ...filters }) => ({
        url: `/users/balance-history/?${queryString.stringify({ page, limit, ...filters }, { skipNull: false, skipEmptyString: true })}`,
        method: "GET"
      }),
      providesTags: (
        result,
        _,
        params
      ): readonly { type: typeof TAGS.USER; id: string }[] =>
        result
          ? [
              {
                type: TAGS.USER as const,
                id: `balance-history:page:${params.page}:limit:${params.limit}`
              },
              { type: TAGS.USER as const, id: "BALANCE_HISTORY" }
            ]
          : [{ type: TAGS.USER as const, id: "BALANCE_HISTORY" }]
    }),
    getTotalStarsEarned: builder.query<number, void>({
      query: () => ({
        url: `/users/total-stars-earned/`,
        method: "GET"
      }),
      providesTags: () => [
        {
          type: TAGS.USER as const,
          id: "TOTAL-STARS-EARNED"
        }
      ]
    }),
    getUserBoughtGold: builder.query<number, void>({
      query: () => ({
        url: "/users/bought-gold",
        method: "GET"
      }),
      providesTags: () => [
        {
          type: TAGS.USER as const,
          id: "BOUGHT_GOLD"
        }
      ]
    }),
    getUserStats: builder.query<IUserStats, { userId: string }>({
      query: ({ userId }) => ({
        url: `/users/stats/${userId}`,
        method: "GET"
      }),
      providesTags: (_, __, { userId }) => {
        return [
          {
            type: TAGS.USER,
            id: `USER_LEADERBOARD_STATS_${userId}`
          }
        ]
      }
    }),
    getUserLeaderboardShortInfo: builder.query<
      IUserLeaderboardShort,
      { userId: string }
    >({
      query: ({ userId }) => ({
        url: `/users/short-info/${userId}`,
        method: "GET"
      }),
      providesTags: (_, __, { userId }) => {
        return [
          {
            type: TAGS.USER,
            id: `USER_LEADERBOARD_SHORT_${userId}`
          }
        ]
      }
    }),
    markUserGuiHasRead: builder.mutation<any, void>({
      query: () => ({
        url: `/users/read-guid`,
        method: "POST",
        data: {}
      }),
      extraOptions: { maxRetries: 2 }
    }),
    getUserGameStatistics: builder.query<IUserStatistics, void>({
      query: () => ({
        url: `/users/game-statistics/`,
        method: "GET"
      }),
      providesTags: [
        {
          type: TAGS.USER,
          id: `USER_GAME_STATISTICS`
        }
      ]
    }),
    getUserSettings: builder.query<IUserSettings, void>({
      query: () => ({
        url: `/users/settings/`,
        method: "GET"
      }),
      providesTags: [{ type: TAGS.USER, id: `USER_SETTINGS` }]
    }),
    setLastOpenApp: builder.mutation<any, void>({
      query: () => ({
        url: `${basePath}/set-last-open-app`,
        method: "PATCH",
        data: {}
      }),
      invalidatesTags: () => [{ type: TAGS.USER, id: "LAST_OPEN_APP" }]
    })
  })
})
